import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import CacheService from '../lib/cacheService';
import { messageFromUnknownError } from '../utils/unknownErrorMessage';

interface UseCachedDataOptions<T> {
  cacheKey: string;
  fetchFn: () => Promise<T>;
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean; // Return stale data immediately while fetching fresh
  enabled?: boolean; // Whether to fetch (useful for conditional fetching)
  /** When true, refetch in background when user returns to the tab (e.g. trainer may have assigned new plans) */
  refetchOnVisible?: boolean;
  /** Throttle refetch-on-visible to at most once per this many ms (default 30s) */
  refetchOnVisibleThrottleMs?: number;
  /** When set, refetch in background every N ms while user is on the page (e.g. so trainer assignments appear) */
  refetchIntervalMs?: number;
  /** When set, refetch only when this returns a different value (e.g. dashboard version). Lightweight check before full refetch. */
  checkForUpdates?: () => Promise<string | null>;
  /** When set, version is taken from the fetched data after a fetch so we skip an extra checkForUpdates() call (saves API calls on initial load). */
  getVersionFromData?: (data: T) => string | null;
  /**
   * When both checkForUpdates and getVersionFromData are set, defaults to true:
   * on load, run the lightweight version check before any stale-TTL background refetch;
   * skip full fetchFn if server version matches cached payload version.
   * Explicit false keeps TTL-only stale-while-revalidate behavior.
   */
  versionFirstInvalidation?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
}

interface UseCachedDataResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Silent refetch (no loading UI, no cache invalidation). Use when showing cached data and want fresh data in background. */
  refetchInBackground: () => Promise<void>;
  invalidate: () => void;
  isStale: boolean;
}

/**
 * Hook for fetching and caching data with automatic stale-while-revalidate
 */
export function useCachedData<T>({
  cacheKey,
  fetchFn,
  ttl = 5 * 60 * 1000, // 5 minutes default
  staleWhileRevalidate = true,
  enabled = true,
  refetchOnVisible = false,
  refetchOnVisibleThrottleMs = 30 * 1000, // 30 seconds
  refetchIntervalMs,
  checkForUpdates,
  getVersionFromData,
  versionFirstInvalidation,
  onSuccess,
  onError,
}: UseCachedDataOptions<T>): UseCachedDataResult<T> {
  const useVersionFirst =
    versionFirstInvalidation !== false &&
    typeof checkForUpdates === 'function' &&
    typeof getVersionFromData === 'function';
  // Check cache synchronously during initialization to avoid loading flash
  const initialCacheCheck = (() => {
    if (!enabled) return { data: null, loading: false, isStale: false };
    if (ttl === 0) return { data: null, loading: true, isStale: false }; // ttl 0 = never use cache
    const cached = CacheService.get<T>(cacheKey);
    const stale = cached ? CacheService.isStale(cacheKey) : false;
    return { data: cached, loading: !cached, isStale: stale };
  })();

  const [data, setData] = useState<T | null>(initialCacheCheck.data);
  const [loading, setLoading] = useState(initialCacheCheck.loading);
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(initialCacheCheck.isStale);
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);
  const fetchFnRef = useRef(fetchFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const hasInitializedRef = useRef(false);
  const visibilityChangeRef = useRef(false);
  const lastRefetchOnVisibleRef = useRef(0);
  const checkForUpdatesRef = useRef(checkForUpdates);
  const getVersionFromDataRef = useRef(getVersionFromData);
  const lastKnownVersionRef = useRef<string | null>(null);
  /** If refetch(true) runs while a fetch is in flight, run one more forced fetch when the current one finishes (e.g. pull-to-refresh). */
  const pendingForceRefetchRef = useRef(false);
  const fetchDataRef = useRef<(silent?: boolean, forceRefresh?: boolean) => Promise<void>>(async () => {});

  useEffect(() => {
    checkForUpdatesRef.current = checkForUpdates;
  }, [checkForUpdates]);
  useEffect(() => {
    getVersionFromDataRef.current = getVersionFromData;
  }, [getVersionFromData]);

  // Update refs when props change
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [fetchFn, onSuccess, onError]);

  const fetchData = useCallback(async (silent = false, forceRefresh = false) => {
    if (fetchingRef.current) {
      if (forceRefresh) {
        pendingForceRefetchRef.current = true;
      }
      console.log('⏭️ Skipping fetch - already in progress', { cacheKey });
      return;
    }

    // If forceRefresh is true, invalidate cache first
    if (forceRefresh) {
      CacheService.invalidate(cacheKey);
    }

    fetchingRef.current = true;
    const currentFetchId = ++fetchIdRef.current;
    console.log('🔄 Starting fetch', { cacheKey, silent, forceRefresh, fetchId: currentFetchId });

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      console.log('📡 Calling fetchFn...', { cacheKey, fetchId: currentFetchId });
      const freshData = await fetchFnRef.current();
      console.log('✅ Fetch completed', { cacheKey, hasData: !!freshData, dataType: typeof freshData, fetchId: currentFetchId, isMounted: mountedRef.current });

      // Cache the data first (skip if ttl 0)
      if (ttl > 0) {
        CacheService.set(cacheKey, freshData, { ttl });
        console.log('💾 Data cached', { cacheKey, fetchId: currentFetchId });
      }

      // Update state - React's setState is safe to call even if component unmounted
      // It will just be ignored, but we should still try to update
      setData(freshData);
      setIsStale(false);
      setError(null);
      console.log('✅ State updated with data', { cacheKey, fetchId: currentFetchId, isMounted: mountedRef.current });

      // After successful fetch, set last known version from data if available (avoids extra checkForUpdates API call); otherwise call checkForUpdates
      if (getVersionFromDataRef.current) {
        const version = getVersionFromDataRef.current(freshData);
        lastKnownVersionRef.current = version ?? null;
      } else if (checkForUpdatesRef.current) {
        try {
          const version = await checkForUpdatesRef.current();
          lastKnownVersionRef.current = version ?? null;
        } catch {
          /* ignore */
        }
      }

      if (!silent) {
        setLoading(false);
        console.log('✅ Loading set to false', { cacheKey, fetchId: currentFetchId });
      }

      // Call onSuccess from ref to avoid dependency
      if (onSuccessRef.current) {
        onSuccessRef.current(freshData);
      }
    } catch (err: any) {
      console.error('❌ Fetch error:', { cacheKey, error: err, fetchId: currentFetchId });
      
      const error =
        err instanceof Error ? err : new Error(messageFromUnknownError(err));
      setError(error);

      if (!silent) {
        setLoading(false);
      }

      // Call onError from ref to avoid dependency
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    } finally {
      // Only reset if this is still the current fetch
      if (currentFetchId === fetchIdRef.current) {
        fetchingRef.current = false;
        console.log('🏁 Fetch completed, flag reset', { cacheKey, fetchId: currentFetchId });
        const runPendingForce = pendingForceRefetchRef.current;
        pendingForceRefetchRef.current = false;
        if (runPendingForce) {
          queueMicrotask(() => {
            void fetchDataRef.current(false, true);
          });
        }
      }
    }
  }, [cacheKey, ttl]); // Only depend on cacheKey and ttl to prevent infinite loops

  useLayoutEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Refetch when user returns to tab or on interval; if checkForUpdates is set, refetch only when version changed
  const refetchRef = useRef<() => Promise<void>>(async () => {});
  refetchRef.current = async () => {
    if (checkForUpdatesRef.current) {
      try {
        const current = await checkForUpdatesRef.current();
        if (current === lastKnownVersionRef.current) {
          return; // no change, skip full refetch
        }
      } catch {
        // on error, refetch to be safe
      }
    }
    await fetchData(true, true); // silent force refresh
  };

  // Handle visibility change: either skip refetch (default) or refetch when visible (refetchOnVisible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        visibilityChangeRef.current = true;
      } else if (document.visibilityState === 'visible') {
        if (refetchOnVisible && enabled) {
          const now = Date.now();
          if (now - lastRefetchOnVisibleRef.current >= refetchOnVisibleThrottleMs) {
            lastRefetchOnVisibleRef.current = now;
            refetchRef.current();
          }
        }
        visibilityChangeRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchOnVisible, refetchOnVisibleThrottleMs, enabled]);

  // Version-first: when cache exists, compare checkForUpdates() to getVersionFromData(cache); skip heavy fetch if equal
  useEffect(() => {
    if (!enabled || ttl === 0 || !useVersionFirst) return;

    const cached = CacheService.get<T>(cacheKey);
    if (!cached) return;

    const check = checkForUpdatesRef.current;
    const verFrom = getVersionFromDataRef.current;
    if (!check || !verFrom) return;

    let cancelled = false;
    void (async () => {
      const cachedVer = verFrom(cached);
      lastKnownVersionRef.current = cachedVer ?? null;
      try {
        const serverVer = await check();
        if (cancelled) return;
        if (serverVer === cachedVer) {
          setIsStale(false);
          return;
        }
      } catch {
        if (!cancelled) await fetchData(true, true);
        return;
      }
      if (!cancelled) await fetchData(true, true);
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, ttl, useVersionFirst, fetchData]);

  // Periodic refetch while member is on the page (e.g. trainer may assign new plans)
  useEffect(() => {
    if (!enabled || !refetchIntervalMs || refetchIntervalMs <= 0) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refetchRef.current();
      }
    }, refetchIntervalMs);
    return () => clearInterval(id);
  }, [enabled, refetchIntervalMs]);

  // Load from cache first - only on mount or when cacheKey/enabled changes
  useEffect(() => {
    mountedRef.current = true; // Set mounted when effect runs
    
    if (!enabled) {
      setLoading(false);
      return;
    }

    // If this is a visibility change and we already initialized, don't refetch
    if (hasInitializedRef.current && visibilityChangeRef.current) {
      console.log('⏭️ Skipping refetch on visibility change', { cacheKey });
      visibilityChangeRef.current = false;
      return;
    }

    // If we already loaded from cache synchronously, skip redundant cache check
    if (initialCacheCheck.data) {
      console.log('✅ Already loaded from cache synchronously', { cacheKey });
      hasInitializedRef.current = true;

      // Stale TTL alone does not trigger heavy fetch when version-first is enabled (version effect decides).
      if (
        initialCacheCheck.isStale &&
        staleWhileRevalidate &&
        !visibilityChangeRef.current &&
        !useVersionFirst
      ) {
        console.log('🔄 Cache is stale, fetching in background', { cacheKey });
        fetchData(true); // Silent fetch
      }
      return;
    }

    console.log('🔄 useCachedData effect running', { cacheKey, enabled, hasInitialized: hasInitializedRef.current });

    // Try to load from cache first (fallback if sync check didn't find it)
    // When ttl === 0, never use cache - always fetch fresh
    const cached = ttl === 0 ? null : CacheService.get<T>(cacheKey);
    const stale = cached ? CacheService.isStale(cacheKey) : false;
    console.log('📦 Cache check', { cacheKey, hasCached: !!cached, isStale: stale });

    if (cached) {
      console.log('✅ Loading from cache', { cacheKey });
      setData(cached);
      setIsStale(stale);
      setLoading(false);
      
      // Only mark as initialized if we're not in a visibility change
      if (!visibilityChangeRef.current) {
        hasInitializedRef.current = true;
      }

      // If stale and staleWhileRevalidate is enabled, fetch in background (unless version-first handles it)
      // But only if this is the initial load, not a visibility change
      if (stale && staleWhileRevalidate && !visibilityChangeRef.current && !useVersionFirst) {
        console.log('🔄 Cache is stale, fetching in background', { cacheKey });
        fetchData(true); // Silent fetch
      } else if (!stale) {
        // Data is fresh, no need to fetch
        console.log('✅ Cache is fresh, no fetch needed', { cacheKey });
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
        }
        return;
      }
    } else {
      console.log('❌ No cache found, fetching...', { cacheKey });
    }

    // Fetch if no cache or if not using stale-while-revalidate
    // But only if this is not a visibility change
    if ((!cached || !staleWhileRevalidate) && !visibilityChangeRef.current) {
      fetchData(false);
      hasInitializedRef.current = true;
    }
    
    return () => {
      console.log('🧹 Cleaning up useCachedData', { cacheKey });
      // Don't set mountedRef to false here - let the unmount effect handle it
    };
  }, [cacheKey, enabled, useVersionFirst]); // staleWhileRevalidate intentionally omitted to limit reruns

  const refetch = useCallback(async () => {
    // Force refresh by passing forceRefresh flag
    await fetchData(false, true);
  }, [fetchData]);

  const refetchInBackground = useCallback(async () => {
    await fetchData(true, false);
  }, [fetchData]);

  const invalidate = useCallback(() => {
    CacheService.invalidate(cacheKey);
    setData(null);
    setIsStale(false);
  }, [cacheKey]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    refetchInBackground,
    invalidate,
    isStale,
  };
}

